// 당첨번호 관리 — 컨테이너 컴포넌트 (모바일 최적화)
// src/pages/admin/components/DrawManager.tsx

import { useDrawManager } from '@/hooks/useDrawManager';
import { getLatestExpectedRound } from '@/utils/lottoUtils';
import type { DrawMode } from '@/pages/admin/components/draw/types';
import ExcelImporter from '@/pages/admin/components/draw/ExcelImporter';
import ApiSyncer from '@/pages/admin/components/draw/ApiSyncer';
import DrawForm from '@/pages/admin/components/draw/DrawForm';
import DrawList from '@/pages/admin/components/draw/DrawList';
import SyncLogList from '@/pages/admin/components/draw/SyncLogList';

const modeLabels: { key: DrawMode; label: string }[] = [
  { key: 'excel', label: '엑셀 업로드' },
  { key: 'api', label: 'API 연동' },
  { key: 'single', label: '단일 등록' },
  { key: 'bulk', label: '일괄 등록' },
];

export default function DrawManager() {
  const dm = useDrawManager();

  return (
    <div className="space-y-6">
      {/* DB Summary & Quick Actions Bar */}
      <div className="bg-background-50 rounded-2xl border border-background-200 p-4 md:p-5">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 md:gap-4">
          <div>
            <h4 className="font-heading text-sm text-foreground-700 mb-1">DB 현황</h4>
            <p className="text-xs text-foreground-500">
              {dm.dbSummary ? (
                <>등록 회차: <strong className="text-foreground-800">{dm.dbSummary.min}~{dm.dbSummary.max}회</strong> ({dm.dbSummary.count}건) | 예상 최신: <strong className="text-foreground-800">{getLatestExpectedRound()}회</strong></>
              ) : (
                '로딩 중...'
              )}
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-wrap">
            <button
              onClick={dm.toggleAutoSync}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                dm.autoSyncEnabled
                  ? 'bg-secondary-100 border-secondary-300 text-secondary-700'
                  : 'bg-background-100 border-background-300 text-foreground-400'
              }`}
            >
              <span className={`w-3 h-3 rounded-full ${dm.autoSyncEnabled ? 'bg-secondary-500' : 'bg-background-400'}`}></span>
              자동 동기화 {dm.autoSyncEnabled ? 'ON' : 'OFF'}
            </button>

            <button
              onClick={dm.handleSyncLatest}
              disabled={dm.syncLatestLoading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-xs font-medium transition-all disabled:opacity-50 cursor-pointer whitespace-nowrap"
            >
              {dm.syncLatestLoading ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  동기화 중...
                </>
              ) : (
                <>
                  <i className="ri-refresh-line text-sm"></i>
                  최신 회차 동기화
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mode Toggle — horizontal scroll on mobile */}
      <div className="bg-background-100 p-1 rounded-xl overflow-x-auto scrollbar-hide">
        <div className="flex gap-1">
          {modeLabels.map((m) => (
            <button
              key={m.key}
              onClick={() => dm.setMode(m.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap flex-shrink-0 ${
                dm.mode === m.key ? 'bg-background-50 text-foreground-800 shadow-sm' : 'text-foreground-400'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      {/* Alerts */}
      {dm.error && (
        <div className="p-3 rounded-lg bg-accent-50 border border-accent-200 flex items-start gap-2 whitespace-pre-line">
          <i className="ri-error-warning-line text-accent-600 text-sm mt-0.5"></i>
          <p className="text-accent-700 text-sm">{dm.error}</p>
        </div>
      )}
      {dm.success && (
        <div className="p-3 rounded-lg bg-secondary-50 border border-secondary-200 flex items-start gap-2 whitespace-pre-line">
          <i className="ri-check-line text-secondary-600 text-sm mt-0.5"></i>
          <p className="text-secondary-700 text-sm">{dm.success}</p>
        </div>
      )}

      {/* Mode Content */}
      {dm.mode === 'excel' && (
        <ExcelImporter
          excelFile={dm.excelFile}
          excelPreview={dm.excelPreview}
          excelParsing={dm.excelParsing}
          excelImporting={dm.excelImporting}
          excelImportProgress={dm.excelImportProgress}
          excelDuplicateMode={dm.excelDuplicateMode}
          excelImportPlan={dm.excelImportPlan}
          duplicateChecking={dm.duplicateChecking}
          duplicateCheckError={dm.duplicateCheckError}
          onDuplicateModeChange={dm.setExcelDuplicateMode}
          fileInputRef={dm.fileInputRef}
          dbSummary={dm.dbSummary}
          autoSyncEnabled={dm.autoSyncEnabled}
          error={dm.error}
          success={dm.success}
          onFileSelect={dm.handleFileSelect}
          onImport={dm.handleExcelImport}
          onClear={dm.handleClearExcel}
        />
      )}

      {dm.mode === 'api' && (
        <ApiSyncer
          apiStartRound={dm.apiStartRound}
          apiEndRound={dm.apiEndRound}
          apiProgress={dm.apiProgress}
          apiRunning={dm.apiRunning}
          retryRounds={dm.retryRounds}
          onStartRoundChange={dm.setApiStartRound}
          onEndRoundChange={dm.setApiEndRound}
          onFetch={dm.handleApiFetch}
          onAbort={dm.handleAbortApi}
          onRetryFailedRounds={dm.handleRetryFailedRounds}
        />
      )}

      {(dm.mode === 'single' || dm.mode === 'bulk') && (
        <DrawForm
          mode={dm.mode}
          submitting={dm.submitting}
          drawId={dm.drawId}
          drawDate={dm.drawDate}
          drawNumbers={dm.drawNumbers}
          drawBonus={dm.drawBonus}
          drawPrize={dm.drawPrize}
          drawWinners={dm.drawWinners}
          onDrawIdChange={dm.setDrawId}
          onDrawDateChange={dm.setDrawDate}
          onDrawNumbersChange={dm.setDrawNumbers}
          onDrawBonusChange={dm.setDrawBonus}
          onDrawPrizeChange={dm.setDrawPrize}
          onDrawWinnersChange={dm.setDrawWinners}
          onSingleSubmit={dm.handleSingleSubmit}
          bulkText={dm.bulkText}
          onBulkTextChange={dm.setBulkText}
          onBulkSubmit={dm.handleBulkSubmit}
        />
      )}

      {/* Draw List */}
      <DrawList
        draws={dm.draws}
        loading={dm.loading}
        onDelete={dm.handleDelete}
        currentPage={dm.drawsPage}
        totalCount={dm.drawsTotal}
        pageSize={dm.drawsPageSize}
        onPageChange={dm.onDrawsPageChange}
      />

      {/* Sync History */}
      <SyncLogList
        syncLogs={dm.syncLogs}
        logsLoading={dm.logsLoading}
        currentPage={dm.syncLogsPage}
        totalCount={dm.syncLogsTotal}
        pageSize={dm.syncLogsPageSize}
        onPageChange={dm.onSyncLogsPageChange}
      />
    </div>
  );
}
